import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useApp } from "../../../context/AppContext";
import { toast } from "../../../components/ui/sonner";
import { X, Loader2, Timer, GitBranch, Landmark, ListChecks, ShieldCheck, CalendarClock, RotateCcw, Search, UserCheck } from "lucide-react";
import { T, pickL } from "../../../lib/i18n";
import { bustDiscAgencyCache, discStatusLabel, notifyV3MetaChanged, PROJECT_HIDDEN_STAGE_KEYS, DISC_TO_TASK } from "./meta";
// 【2026-10-22】「设计总周期」旁边的「调整阶段工期」打开的就是这个弹窗。
// 后端 /design/v3/time-cfg 早就在，此前只差这一层界面，文案却已经指向它了。
import { StageDaysDialog } from "./StageDaysDialog";

/* 签约前阶段在「签约后」视角下的展示过滤（2026-10-20 用户反馈）。
   需求原话：「详情都没显示这个了..这里还显示」——项目详情阶段页签与项目矩阵
   从 d8205601 起已不再展示「前期方案咨询 / 合同签署」，但本配置弹窗仍在
   无条件列出这两步，看起来像流程里还留着它们。
   这里与 meta.isStageHiddenInProjects 同一份口径，仅做展示过滤：
   阶段实例、任务责任配置、产物挂载都不删 —— 签约前项目仍按原配置自动分工，
   切回「签约前营销方案」时详情页照样完整展示这两步。 */
const hidePreSignStage = (key) => PROJECT_HIDDEN_STAGE_KEYS.includes(key);

// 「全流程责任」里任务身份的后端未返回时的兜底文案（正常走后端 taskRoles）；
// manager 是阶段任务独有的「设计部主管」——它不绑定项目三岗，只表示主管认领。
const RESP_FALLBACK = { us: "美国建筑设计师", cn: "中国建筑设计师", submitter: "交图员", manager: "设计部主管" };

/**
 * 全局配置弹窗（仅 admin/CEO 可进；后端二次校验）。
 *
 * 配置统一收拢在「项目管理 → 产物时间设置」，按业务对象拆分页签：
 *   · 时间 / 流程↔产物 / 产物类型↔机构 / 产物↔状态 / 全流程责任 / 移交资料 / 项目延期。
 *   2026-10-19 新增「产物↔状态」：原来 12 个状态写死并展示给所有产物，
 *   人工办理项会看到现场测量、Plan Check 等无关选项。状态键与自动化副作用保持不变，
 *   只把“哪些状态属于哪项产物”变成主管可维护配置，旧项目当前状态仍兼容。
 *   2026-10-19 新增「产物提交 · 审核」：按阶段列出产物，逐项配置由哪个**身份**
 *   （美国设计师 / 中国设计师 / 交图员）提交、由哪个**身份**审核。配置的是身份不是具体人——
 *   一份配置对所有项目生效，具体落到谁由项目的三个岗位决定。
 *   ⚠️ 审核不是第四个身份：能选来审核的人，仍然只来自这三岗身份本身；
 *   审核人全不选 = 无需审核（提交即完成）。
 *   2026-10-20「延期设置」只留项目级：产物级延期随旧产物会话抽屉下线已无入口，
 *   留着只会让人配一个永远不生效的规则；项目级延期仍在实际使用（目标完成日过期后申请）。
 */
export const DiscCfgDialog = ({ api, onClose }) => {
  const { lang } = useApp();
  const zh = lang === "zh";
  const [tab, setTab] = useState("time");
  const [hours, setHours] = useState("");
  const [days, setDays] = useState({});
  const [canEditTime, setCanEditTime] = useState(true);   // 响应时间 / 默认工期只有 admin·CEO 能改
  // 【2026-10-22】设计总周期（工作日）：只读，由「阶段工期」自动汇总，不再手填。
  const [designDays, setDesignDays] = useState("—");
  const [stageDlg, setStageDlg] = useState(false);   // 「调整阶段工期」弹窗
  const [discs, setDiscs] = useState([]);
  const [busy, setBusy] = useState(false);
  // ② 流程 ↔ 产物
  const [stages, setStages] = useState([]);
  const [map, setMap] = useState({});
  const [removed, setRemoved] = useState([]);
  const [mapDirty, setMapDirty] = useState(false);
  const [flowMode, setFlowMode] = useState("disc");   // disc = 按产物配（13 行，最快）；stage = 按阶段配（看全貌）
  const [flowQ, setFlowQ] = useState("");
  // ① 时间：批量填充
  const [bulkDay, setBulkDay] = useState("");
  // ③ 移交资料
  const [gate, setGate] = useState(null);
  const [gateReq, setGateReq] = useState({});
  const [gateLabels, setGateLabels] = useState({});
  // ④ 产物 ↔ 机构（展示层关联，2026-10-18 做成可配置）
  const [daMap, setDaMap] = useState({});
  const [daTypes, setDaTypes] = useState([]);
  const [discKinds, setDiscKinds] = useState({});
  const [daQ, setDaQ] = useState("");
  // ⑤ 产物 ↔ 状态（2026-10-19）：每项产物只选择自己业务上会发生的状态。
  const [dsMap, setDsMap] = useState({});
  const [dsDefault, setDsDefault] = useState({});
  const [dsStatuses, setDsStatuses] = useState([]);
  const [dsStatusMeta, setDsStatusMeta] = useState({});
  // 产物状态也按阶段分组（2026-10-19 用户需求「产物状态也是…」）：
  // 后端返回的 stages 与「全流程责任」页签同源，两页阶段顺序和详情页一致。
  const [dsStages, setDsStages] = useState([]);
  const [dsQ, setDsQ] = useState("");
  // ⑥ 今日代办门禁（原来只能改库：enabled + 会被拦下班打卡的岗位）
  const [todoCfg, setTodoCfg] = useState(null);
  const [gateRoles, setGateRoles] = useState([]);
  // ⑦ 延期设置：现在只剩「项目整体」一级。0 = 不限制。
  //    原来还有「设计师单产物」一级，随产物会话抽屉下线已经没有任何入口，2026-10-20 从本页移除。
  //    ⚠️ 保存时只提交 project，不提交 disc：后端 PUT 是按字段合并，
  //    这样旧库里已配过的 disc 值不会被这次保存顺手清零（仍有历史审批单依赖它）。
  const [extCfg, setExtCfg] = useState({ project: { maxDays: "0", leadDays: "0", maxTimes: "0" } });
  const [extRo, setExtRo] = useState(false);
  // ⑧ 全流程责任分工（2026-10-19 用户需求）：按阶段列出三类对象各自的负责人 ——
  //    交付物（谁提交 / 谁审核）、机构（谁办理=给到外部 / 谁推进）、阶段任务（谁负责推进）。
  const [prMap, setPrMap] = useState({});
  const [prStages, setPrStages] = useState([]);
  const [prRoles, setPrRoles] = useState([]);
  const [prTaskRoles, setPrTaskRoles] = useState([]);
  // 机构：handleRoles 办理（把资料交到外部机构）/ advanceRoles 推进（跟进催办盯批复）
  const [agResp, setAgResp] = useState({});
  // 阶段任务：ownerRoles 负责推进（manager = 设计部主管认领）
  const [taskResp, setTaskResp] = useState({});
  const [prQ, setPrQ] = useState("");
  const [prRo, setPrRo] = useState(false);
  // ⑨ City 审图整改（2026-10-20 用户需求）：每新增一轮顺延的项目天数 + 每个审图阶段
  //    「谁交修改图纸 / 谁交回复函」。配的是身份，具体是谁由项目三岗决定。
  const [crCfg, setCrCfg] = useState(null);
  const [crStages, setCrStages] = useState([]);
  const [crSlots, setCrSlots] = useState([]);
  const [crRoles, setCrRoles] = useState([]);
  const [crRo, setCrRo] = useState(false);
  // ⑩ Land Survey 可选块（2026-10-20 用户需求）：谁负责跟进测绘 + 默认预计天数。
  //    实际天数由项目负责人在方案设计阶段块里按项目填，这里只给预填值与责任人。
  const [lsCfg, setLsCfg] = useState(null);
  const [lsRoles, setLsRoles] = useState([]);
  const [lsRo, setLsRo] = useState(false);
  // 签约前两步（前期方案咨询 / 合同签署）在「全流程责任」里默认折叠，
  // 与项目详情 / 矩阵的隐藏口径一致；需要时点按钮展开再配责任人。
  const [showPreSign, setShowPreSign] = useState(false);
  /* 各个页签是否成功读到过数据。保存时只提交「读到过的」那几份 ——
     否则某个 GET 失败后 state 还是初始值（map={}），一按保存就把线上映射清空。 */
  const [ok, setOk] = useState({ time: false, flow: false, gate: false, da: false, ds: false, todo: false, ext: false, pr: false, cr: false, ls: false });

  useEffect(() => {
    api.get("/design/disc-cfg")
      .then(({ data }) => { setHours(String(data.respondHours ?? 12)); setDays({ ...(data.slaDays || {}) }); setDesignDays(String(data.designDays ?? "—")); setCanEditTime(data.canEdit !== false); setOk((v) => ({ ...v, time: true })); })
      .catch((e) => toast.error(e?.response?.data?.detail || e.message));
    api.get("/design/v3/meta").then(({ data }) => setDiscs(data.disciplines || [])).catch(() => {});
    api.get("/design/todo/meta").then(({ data }) => {
      setTodoCfg({ enabled: data.enabled !== false });
      setGateRoles(data.gateRoles || []);
      setOk((v) => ({ ...v, todo: true }));
    }).catch(() => {});
    api.get("/design/v3/stage-map").then(({ data }) => {
      setStages(data.stages || []);
      setMap({ ...(data.map?.disciplines || {}) });
      setRemoved([...((data.disabled || {}).disciplines || [])]);
      setMapDirty(false);
      setOk((v) => ({ ...v, flow: true }));
    }).catch(() => {});
    api.get("/design/v3/settings/handoff-gate").then(({ data }) => {
      setGate(data); setGateReq({ ...(data.require || {}) }); setGateLabels({ ...(data.labels || {}) });
      setOk((v) => ({ ...v, gate: true }));
    }).catch(() => {});
    api.get("/design/v3/settings/disc-agency-map").then(({ data }) => {
      setDaMap({ ...(data.map || {}) });
      setDaTypes(data.agencyTypes || []);
      setDiscKinds({ ...(data.kinds || {}) });
      setOk((v) => ({ ...v, da: true }));
    }).catch(() => {});
    api.get("/design/v3/settings/disc-status-map").then(({ data }) => {
      setDsMap({ ...(data.map || {}) });
      setDsDefault({ ...(data.default || {}) });
      setDsStatuses(data.statuses || []);
      setDsStatusMeta(data.statusMeta || {});
      setDsStages(data.stages || []);
      setOk((v) => ({ ...v, ds: true }));
    }).catch(() => {});
    api.get("/design/v3/settings/extend-cfg").then(({ data }) => {
      setExtCfg({
        project: { maxDays: String(data.project?.maxDays ?? 0), leadDays: String(data.project?.leadDays ?? 0), maxTimes: String(data.project?.maxTimes ?? 0) },
      });
      setExtRo(!data.isManager);
      setOk((v) => ({ ...v, ext: true }));
    }).catch(() => {});
    /* 全流程责任分工（2026-10-19）：按阶段分组的产物 + 机构 + 阶段任务，
       以及三类对象的当前配置。只读权限由后端 isManager 决定，与其它页签同一口径。 */
    api.get("/design/v3/settings/disc-party-roles").then(({ data }) => {
      setPrMap({ ...(data.map || {}) });
      setPrStages(data.stages || []);
      setPrRoles(data.roles || []);
      setPrTaskRoles(data.taskRoles || data.roles || []);
      setAgResp({ ...(data.agencyMap || {}) });
      setTaskResp({ ...(data.taskMap || {}) });
      setPrRo(!data.isManager);
      setOk((v) => ({ ...v, pr: true }));
    }).catch(() => {});
    /* City 审图整改配置（2026-10-20 用户需求）：
       「整体项目进度延期 3 天……还需要在图一中的配置中增加这个 correction 的责任人的配置」。
       与全流程责任同页展示 —— 它本质就是「整改这件事谁来做」，拆到别的页签反而找不到。 */
    api.get("/design/v3/settings/correction-cfg").then(({ data }) => {
      setCrCfg(data.cfg || null);
      setCrStages(data.stages || []);
      setCrSlots(data.slots || []);
      setCrRoles(data.roles || []);
      setCrRo(!data.isManager);
      setOk((v) => ({ ...v, cr: true }));
    }).catch(() => {});
    /* Land Survey 可选块（2026-10-20 用户需求）：
       「单独显示在下面的前期先放宽一点，然后让他们自己能够配，他们就是三个负责人」
       「就加在之前的全流程责任里面」—— 所以配置落在这个页签，与 City 整改并列。 */
    api.get("/design/v3/settings/land-survey-cfg").then(({ data }) => {
      setLsCfg(data.cfg || null);
      setLsRoles(data.roles || []);
      setLsRo(!data.isManager);
      setOk((v) => ({ ...v, ls: true }));
    }).catch(() => {});
  }, [api]);

  const assign = (discKey, stageKey) => {
    setMap((m) => ({ ...m, [discKey]: stageKey }));
    setRemoved((r) => r.filter((k) => k !== discKey));
    setMapDirty(true);
  };
  const unset = (discKey) => {
    setMap((m) => { const n = { ...m }; delete n[discKey]; return n; });
    setRemoved((r) => (r.includes(discKey) ? r : [...r, discKey]));
    setMapDirty(true);
  };

  /* 一次保存全部页签。原来「保存」只提交当前页签，用户在「时间」改完切到「流程」再按保存，
     时间的改动会静默丢掉 —— 这类丢改动的问题比多提交几个 PUT 严重得多。
     ok.xxx 为 false 的页签直接跳过（GET 没成功过，state 不可信）。 */
  const save = async () => {
    setBusy(true);
    try {
      const jobs = [];
      // 【2026-10-22】不再提交 designDays：总周期已改为按阶段工期自动汇总，
      // 后端会忽略该字段（见 design_disc_chat.disc_cfg_put）。
      if (ok.time && canEditTime) jobs.push(api.put("/design/disc-cfg", { respondHours: Number(hours) || 12, slaDays: days }));
      if (ok.flow) jobs.push(api.put("/design/v3/stage-map", { disciplines: map, disabled: { disciplines: removed } }));
      if (ok.gate) jobs.push(api.put("/design/v3/settings/handoff-gate", { require: gateReq, labels: gateLabels }));
      if (ok.da) jobs.push(api.put("/design/v3/settings/disc-agency-map", { map: daMap, kinds: discKinds }));
      if (ok.ds) jobs.push(api.put("/design/v3/settings/disc-status-map", { map: dsMap }));
      if (ok.todo && todoCfg) jobs.push(api.put("/design/todo/settings", { enabled: !!todoCfg.enabled, gateRoles }));
      // 延期规则只提交项目级：产物级配置已从本页移除（见 ext 页签注释）。
      // 后端 PUT 是按字段合并（cur 打底再覆盖 body 里给过的字段），
      // 这里不传 disc，库里已有的 disc 规则原样保留，不会被这次保存清空。
      if (ok.ext && !extRo) jobs.push(api.put("/design/v3/settings/extend-cfg", {
        project: { maxDays: Number(extCfg.project.maxDays) || 0, leadDays: Number(extCfg.project.leadDays) || 0, maxTimes: Number(extCfg.project.maxTimes) || 0 },
      }));
      // 全流程责任：提交/审核、机构办理/推进、阶段任务负责推进一次提交。
      // 三块都是完整表，后端会再归一化一次并逐项校验。
      if (ok.pr && !prRo) jobs.push(api.put("/design/v3/settings/disc-party-roles", { map: prMap, agencyMap: agResp, taskMap: taskResp }));
      // City 整改：顺延天数 + 两个审图阶段各自「谁交图纸 / 谁交回复函」。
      // 只提交读到过的那份（ok.cr），避免 GET 失败后把线上配置清空。
      if (ok.cr && !crRo && crCfg) jobs.push(api.put("/design/v3/settings/correction-cfg", crCfg));
      // Land Survey：责任人 + 跟进间隔。同样只在读到过配置时才提交。
      // followupDays（2026-10-20 第二轮需求「每三天一个待办」）也在这里配，
      // 原因同上一轮用户要求「让他们自己能够配」—— 测绘周期长的项目可把间隔放大。
      // 不再提交 defaultDays：用户看到界面后明确否掉了「预计天数」，
      // 工期改为只在标记完成时按实际交付天数顺延，后端也不再读这个字段。
      if (ok.ls && !lsRo && lsCfg) jobs.push(api.put("/design/v3/settings/land-survey-cfg", {
        followupDays: Number(lsCfg.followupDays) || 3,
        roles: lsCfg.roles || [],
      }));
      await Promise.all(jobs);
      if (ok.da) bustDiscAgencyCache();   // 让阶段页签 / 矩阵弹窗下次打开时拿到新映射
      if (ok.ds) notifyV3MetaChanged();    // 当前页面立即换到新的产物状态口径
      setMapDirty(false);
      toast.success(T("设置已保存"));
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  const TABS = [["time", T("时间"), Timer], ["flow", T("流程 ↔ 产物"), GitBranch], ["discag", T("产物类型 ↔ 机构"), Landmark], ["discstat", T("产物 ↔ 状态"), ListChecks], ["party", T("全流程责任"), UserCheck], ["handoff", T("移交资料"), ShieldCheck], ["ext", T("项目延期"), CalendarClock]];
  const flowQn = flowQ.trim().toLowerCase();
  const discHit = (d) => !flowQn || `${d.name} ${d.zh || ""}`.toLowerCase().includes(flowQn);
  const stageHit = (s) => !flowQn || String(s.name || "").toLowerCase().includes(flowQn);
  const mountedN = discs.filter((d) => map[d.key] && !removed.includes(d.key)).length;
  const unmountedN = discs.length - mountedN;
  const kindOf = (d) => discKinds[d.key] || d.discKind || "design";
  const statusLabel = (status, discKind) => discStatusLabel({ discStatusMeta: dsStatusMeta }, status, discKind);
  const addDiscStatus = (key, status) => {
    if (!status) return;
    setDsMap((current) => {
      const selected = new Set([...(current[key] || []), status]);
      return { ...current, [key]: dsStatuses.filter((x) => selected.has(x)) };
    });
  };
  const removeDiscStatus = (key, status) => {
    const selected = dsMap[key] || [];
    if (selected.length <= 1) {
      toast.error(T("每项产物至少保留一个状态"));
      return;
    }
    setDsMap((current) => {
      const next = (current[key] || []).filter((x) => x !== status);
      return { ...current, [key]: dsStatuses.filter((x) => next.includes(x)) };
    });
  };
  const resetDiscStatus = (key) => {
    const defaults = dsDefault[key];
    if (Array.isArray(defaults) && defaults.length) setDsMap((current) => ({ ...current, [key]: [...defaults] }));
  };

  /* ---------- 全流程责任分工（2026-10-19 用户需求） ----------
     用户目标：「所有的东西还需要明确是谁来提交..谁来推进..谁来给到外部这些」。
     三类对象同处一个阶段标题下，责任项按对象类型区分（不搞统一四列的空配置）：
       · 交付物：谁来提交 / 谁来审核（审核全不选 = 无需审核，提交即完成）
       · 机构：  谁来办理（把资料交到外部机构）/ 谁来推进（跟进、催办、盯批复）
       · 阶段任务：谁来负责推进（多一个「设计部主管」，表示由主管认领、不自动落到具体人） */
  const prOf = (key) => prMap[key] || { submitRoles: [], reviewRoles: [] };
  const agOf = (type) => agResp[type] || { handleRoles: [], advanceRoles: [] };
  const taskOf = (key) => taskResp[key] || { ownerRoles: [] };
  // 通用胶囊开关：三个责任面板共用，避免各写一份样式与 state 更新逻辑。
  const togglePill = (setter, key, field, role) => {
    setter((cur) => {
      const cfg = { ...(cur[key] || {}) };
      const list = cfg[field] || [];
      cfg[field] = list.includes(role) ? list.filter((x) => x !== role) : [...list, role];
      return { ...cur, [key]: cfg };
    });
  };
  const toggleRole = (key, field, role) => togglePill(setPrMap, key, field, role);
  const prQn = prQ.trim().toLowerCase();
  const roleLabel = (key) => (prRoles.find((r) => r.key === key)?.label) || key;
  const taskRoleLabel = (key) => (prTaskRoles.find((r) => r.key === key)?.label) || RESP_FALLBACK[key] || key;
  const discLabel = (key) => {
    const d = discs.find((x) => x.key === key);
    return d ? pickL(d, "zh", "name") : key;
  };
  // 校验：交付物必须有提交身份；机构必须有办理与推进；任务必须有负责推进。
  // 三项在保存前就提示，避免用户点了保存才吃 400。
  /* 签约前阶段（前期方案咨询 / 合同签署）默认折叠（2026-10-20 用户反馈
     「详情都没显示这个了..这里还显示」）：
       · 项目详情阶段页签和项目矩阵从 d8205601 起对签约后项目隐藏这两步，
         但本弹窗无条件列出，用户会误以为流程仍在要求它们；
       · 这两步对「签约前营销方案」项目仍然真实存在（政策核实 / 合同附件门禁都在跑），
         所以不能从配置里删掉 —— 折叠只是默认不展示，点「显示」仍可调责任人；
       · 折叠后保存不受影响：prMap / taskResp 仍是完整表，隐藏项按原值提交，
         后端归一化也不会把没展示过的配置清掉。
     校验与计数只算当前可见阶段，避免出现「提示有几项没配齐、页面上却找不到」的死角。 */
  const preSignStages = prStages.filter((s) => hidePreSignStage(s.key));
  const prVisible = showPreSign ? prStages : prStages.filter((s) => !hidePreSignStage(s.key));
  /* 被配对任务行承载的产物不单独列出（2026-10-21 用户要求「全阶段责任设置里面也是」）。
     口径与详情阶段卡 / 矩阵阶段会话一致：本阶段有配对任务 → 产物行收起，只在任务行里
     只读显示它配好的提交 / 审核身份。
     下面的校验与计数必须跟着只算**看得到的**项，否则会出现「提示 N 项没配齐、
     页面上却找不到那一项」的死角（与签约前阶段折叠同一个坑，见上方说明）。 */
  const carriedInStage = (s) => {
    const titles = new Set((s.tasks || []).map((t) => t.title));
    return new Set((s.disciplines || []).filter((k) => titles.has(DISC_TO_TASK[k])));
  };
  const prNoSubmit = prVisible.flatMap((s) => {
    const carried = carriedInStage(s);
    return (s.disciplines || []).filter((k) => !carried.has(k));
  }).filter((k) => !(prOf(k).submitRoles || []).length);
  const agEmpty = prVisible.flatMap((s) => s.agencies || [])
    .filter((t) => !(agOf(t).handleRoles || []).length || !(agOf(t).advanceRoles || []).length);
  const taskEmpty = prVisible.flatMap((s) => s.tasks || [])
    .filter((t) => !(taskOf(t.key).ownerRoles || []).length);
  const prTotal = prVisible.reduce((n, s) => n + ((s.disciplines || []).length - carriedInStage(s).size) + (s.agencies || []).length + (s.tasks || []).length, 0);

  return createPortal(
    <div className="fixed inset-0 z-[85] flex items-center justify-center p-4" data-testid="dv3-disccfg-dialog">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-3xl p-4 max-h-[88vh] flex flex-col">
        <div className="flex items-center gap-2">
          <Timer className="h-4 w-4 text-amber-600" />
          <b className="text-sm text-slate-800">{T("产物与流程设置")}</b>
          <button onClick={onClose} className="ml-auto text-slate-400 hover:text-slate-900" data-testid="dv3-disccfg-close"><X className="h-4 w-4" /></button>
        </div>

        <div className="mt-2 inline-flex flex-wrap rounded-lg bg-slate-100 p-0.5 self-start" data-testid="dv3-disccfg-tabs">
          {TABS.map(([k, l, I]) => (
            <button key={k} type="button" data-testid={`dv3-disccfg-tab-${k}`} onClick={() => setTab(k)}
              className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-md ${tab === k ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>
              <I className="h-3.5 w-3.5" />{l}
            </button>
          ))}
        </div>

        <div className="mt-3 overflow-y-auto pr-0.5 flex-1">
          {tab === "time" && <>
            <p className="text-[11px] text-slate-400">{T("这里改的是「默认值」，只用于新建产物时预填；不会回溯已配置时间的产物。")}</p>
            {!canEditTime && <p className="mt-1.5 text-[11px] text-amber-700" data-testid="dv3-disccfg-time-ro">{T("响应时间与默认工期只有 admin / CEO 能改；本页其余设置（流程、产物↔机构、移交资料、打卡门禁）你可以直接保存。")}</p>}
            <div className="mt-3 flex items-center gap-2 text-xs flex-wrap">
              <span className="text-slate-600 font-semibold">{T("响应时间")}</span>
              <input type="number" min={0.02} max={240} step="0.01" value={hours} onChange={(e) => setHours(e.target.value)} disabled={!canEditTime}
                className="w-20 border border-slate-200 rounded-md px-2 py-1 text-xs disabled:bg-slate-50 disabled:text-slate-400" data-testid="dv3-disccfg-hours" />
              <span className="text-slate-500">{T("小时（可填小数，如 0.05 = 3 分钟；被 @ 的人超时未回复 → 生成待办，后台每 10 分钟扫描一次）")}</span>
            </div>
            {/* 【2026-10-22 阶段唯一源】兜底总周期改为只读：它就是当前阶段模板的工作日之和，
                原来允许手填等于给同一个事实留两个入口。要改总周期，去「阶段工期」里调。 */}
            <div className="mt-2 flex items-center gap-2 text-xs flex-wrap" data-testid="dv3-disccfg-designdays-row">
              <span className="text-slate-600 font-semibold">{T("设计总周期")}</span>
              <span className="inline-flex items-center justify-center min-w-[5rem] rounded-md bg-slate-50 border border-slate-200 px-2 py-1 text-xs font-bold tabular-nums text-slate-700"
                data-testid="dv3-disccfg-designdays" title={T("由阶段工期自动汇总，不可单独修改")}>{designDays}</span>
              <span className="text-slate-500">{T("个工作日 —— 由「阶段工期」自动汇总，改总周期请调阶段工期。产物工期从项目「目标完成日」倒推；项目没有目标完成日时，才用「合同签署日 + 这个工作日数」兜底。")}</span>
              <button type="button" data-testid="dv3-disccfg-open-stage-days" onClick={() => setStageDlg(true)}
                className="ml-auto inline-flex items-center gap-1 font-semibold text-violet-700 border border-violet-200 rounded-md px-2 py-0.5 text-[11px] hover:bg-violet-50">
                <CalendarClock className="h-3 w-3" />{T("调整阶段工期")}
              </button>
            </div>
            <p className="text-xs font-semibold text-slate-600 mt-4 mb-1 flex items-center gap-2 flex-wrap">
              {T("产物默认工期（天）")}
              <span className="font-normal text-slate-400">{T("新建产物时按这里预填")}</span>
              {/* 批量：13 项一个个填太慢，日常多半是「全套统一 N 天」，填完再单独调例外项 */}
              <span className="ml-auto inline-flex items-center gap-1 font-normal text-[11px] text-slate-500">
                {T("统一设为")}
                <input type="number" min={0} max={365} value={bulkDay} onChange={(e) => setBulkDay(e.target.value)} placeholder="—" disabled={!canEditTime}
                  className="w-14 border border-slate-200 rounded-md px-1.5 py-0.5 text-xs text-slate-800 disabled:bg-slate-50" data-testid="dv3-disccfg-bulk-input" />
                {T("天")}
                <button type="button" data-testid="dv3-disccfg-bulk-apply" disabled={!canEditTime || !String(bulkDay).trim()}
                  onClick={() => setDays(Object.fromEntries(discs.map((d) => [d.key, bulkDay])))}
                  className="font-semibold text-violet-700 border border-violet-200 rounded-md px-1.5 py-0.5 hover:bg-violet-50 disabled:opacity-40">
                  {T("应用到全部")}
                </button>
              </span>
            </p>
            <div className="grid sm:grid-cols-2 gap-1.5">
              {discs.map((d) => (
                <label key={d.key} className="flex items-center gap-2 text-[11px] text-slate-700 rounded-lg border border-slate-100 bg-slate-50 px-2 py-1">
                  <span className="flex-1 truncate">{pickL(d, "zh", "name")}</span>
                  <input type="number" min={0} max={365} value={days[d.key] ?? ""} onChange={(e) => setDays((v) => ({ ...v, [d.key]: e.target.value }))} disabled={!canEditTime}
                    className="w-14 border border-slate-200 rounded-md px-1.5 py-0.5 text-xs disabled:bg-slate-50" data-testid={`dv3-disccfg-day-${d.key}`} />
                  <span className="text-slate-400">{T("天")}</span>
                </label>
              ))}
            </div>

            {/* 下班打卡门禁：此前只能改库（design_todo.settings），经理在界面上看不到也关不掉 */}
            <div className="mt-4 border-t border-slate-100 pt-3" data-testid="dv3-disccfg-gate">
              <label className="flex items-center gap-2 text-xs text-slate-700 font-semibold">
                <input type="checkbox" checked={!!todoCfg?.enabled} disabled={!ok.todo}
                  onChange={(e) => setTodoCfg((c) => ({ ...(c || {}), enabled: e.target.checked }))}
                  data-testid="dv3-disccfg-gate-enabled" />
                {T("未完成代办拦截下班打卡")}
                <span className="font-normal text-slate-400">{T("只拦「未完成且已到截止日」的代办，经理不拦")}</span>
              </label>
              {todoCfg?.enabled && (
                <div className="mt-1.5 flex items-center gap-3 text-[11px] text-slate-600">
                  <span className="text-slate-500">{T("拦截岗位")}</span>
                  {[["cn_architect", T("中国建筑设计师")], ["us_policy", T("美国建筑设计师")]].map(([k, l]) => {
                    const on = gateRoles.includes(k);
                    return (
                      <label key={k} className="inline-flex items-center gap-1">
                        <input type="checkbox" checked={on} disabled={!ok.todo}
                          onChange={(e) => setGateRoles((rs) => e.target.checked ? [...new Set([...rs, k])] : rs.filter((x) => x !== k))}
                          data-testid={`dv3-disccfg-gate-role-${k}`} />
                        {l}
                      </label>
                    );
                  })}
                  {gateRoles.length === 0 && <span className="text-amber-600">{T("未选岗位 = 不拦任何人")}</span>}
                </div>
              )}
            </div>
          </>}

          {tab === "ext" && <>
            {/* 2026-10-20 用户反馈「这个延期设置感觉在当前流程已经不适用了」。
                查证结论：只有「项目延期」这一级还真的在用 ——
                ProjectDrawer 目标完成日过期后调 POST /projects/{pid}/target-extend，
                后端用 project 这组规则校验（单次上限 / 提前窗口 / 次数上限）。
                而原来的「设计师延期（单个产物）」已经没有任何入口：
                读取 disc 规则的只有 design_disc_chat 的产物会话详情与 extend-request，
                而产物会话抽屉在 5334ef33「下线旧产物会话抽屉」里整体删除，
                前端再无任何调用点，产物也已改成只做展示。
                所以这里移除产物级配置，只保留项目级；配置项与服务端校验一一对应，
                不再给用户一个改了却没有入口会走到它的开关。 */}
            <p className="text-[11px] text-slate-400">
              {T("项目目标完成日过期后，由项目经理在项目详情里申请延期（销售总监 / CEO 审批）。这里配置申请规则：单次最多延几天、提前多久才能申请、最多能申请几次。填 0 = 不限制。")}
            </p>
            {extRo && <p className="mt-1.5 text-[11px] text-amber-700" data-testid="dv3-extcfg-ro">{T("延期规则只有设计主管 / admin / CEO 能改。")}</p>}

            <div className="mt-3 rounded-lg border border-slate-200 p-2.5" data-testid="dv3-extcfg-project">
              <div className="flex items-center gap-2 flex-wrap">
                <b className="text-xs text-slate-800">{T("项目延期（整个项目）")}</b>
                <span className="text-[10px] text-slate-400">{T("项目经理发起 → 销售总监 / CEO 审批")}</span>
              </div>
              <div className="mt-2 space-y-1.5 text-xs">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-slate-600 shrink-0">{T("单次最多延")}</span>
                  <input type="number" min={0} max={365} value={extCfg.project.maxDays} disabled={extRo}
                    onChange={(e) => setExtCfg((c) => ({ ...c, project: { ...c.project, maxDays: e.target.value } }))}
                    className="w-20 border border-slate-200 rounded-md px-2 py-1 text-xs disabled:bg-slate-50 disabled:text-slate-400" data-testid="dv3-extcfg-proj-max" />
                  <span className="text-slate-500">{T("天（0 = 不限）")}</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-slate-600 shrink-0">{T("提前申请窗口")}</span>
                  <input type="number" min={0} max={365} value={extCfg.project.leadDays} disabled={extRo}
                    onChange={(e) => setExtCfg((c) => ({ ...c, project: { ...c.project, leadDays: e.target.value } }))}
                    className="w-20 border border-slate-200 rounded-md px-2 py-1 text-xs disabled:bg-slate-50 disabled:text-slate-400" data-testid="dv3-extcfg-proj-lead" />
                  <span className="text-slate-500">{T("天（0 = 随时可申请；填 14 = 只在结束日前 14 天内可申请）")}</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-slate-600 shrink-0">{T("最多可申请")}</span>
                  <input type="number" min={0} max={365} value={extCfg.project.maxTimes} disabled={extRo}
                    onChange={(e) => setExtCfg((c) => ({ ...c, project: { ...c.project, maxTimes: e.target.value } }))}
                    className="w-20 border border-slate-200 rounded-md px-2 py-1 text-xs disabled:bg-slate-50 disabled:text-slate-400" data-testid="dv3-extcfg-proj-times" />
                  <span className="text-slate-500">{T("次（0 = 不限；只算已批准和待审批，被驳回不占用）")}</span>
                </div>
              </div>
            </div>
          </>}

          {tab === "flow" && <>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[11px] text-slate-400 flex-1 min-w-[240px]">
                {T("决定每个阶段需要哪些产物。一个阶段可以挂多个产物；不属于任何阶段 = 系统不再要求。改动对全部项目生效，不影响已上传的文件。")}
              </p>
              <span className="text-[11px] font-semibold text-slate-600 tabular-nums shrink-0" data-testid="dv3-flow-summary">
                {T("已关联 {0}/{1}", [mountedN, discs.length])}
                {unmountedN > 0 && <span className="text-amber-700"> · {T("{0} 项不要求", [unmountedN])}</span>}
              </span>
            </div>
            {/* 工具条：搜索 + 视角切换。「按产物」是主视角（13 行、一行一个下拉，改一项只要一次点击）；
                「按阶段」用来看全貌（哪个阶段要求了哪些产物）。搜索两边共用。 */}
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="h-3.5 w-3.5 absolute left-2 top-1.5 text-slate-400" />
                <input value={flowQ} onChange={(e) => setFlowQ(e.target.value)} placeholder={T("搜产物 / 阶段")} data-testid="dv3-flow-search"
                  className="text-xs border border-slate-200 rounded-md pl-7 pr-2 py-1 w-44" />
              </div>
              <div className="inline-flex rounded-lg bg-slate-100 p-0.5" data-testid="dv3-flow-mode">
                {[["disc", T("按产物")], ["stage", T("按阶段")]].map(([k, l]) => (
                  <button key={k} type="button" data-testid={`dv3-flow-mode-${k}`} onClick={() => setFlowMode(k)}
                    className={`px-2.5 py-1 text-[11px] font-semibold rounded-md ${flowMode === k ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>{l}</button>
                ))}
              </div>
              {flowQn && <button type="button" onClick={() => setFlowQ("")} className="text-[11px] text-violet-700 hover:underline">{T("清除搜索")}</button>}
            </div>

            {flowMode === "disc" ? (
              <div className="mt-2 space-y-1" data-testid="dv3-flow-by-disc">
                {discs.filter(discHit).map((d) => {
                  const cur = removed.includes(d.key) ? "" : (map[d.key] || "");
                  return (
                    <div key={d.key} className={`flex items-center gap-2 rounded-md border px-2 py-1 ${cur ? "border-slate-100 bg-slate-50/70" : "border-amber-200 bg-amber-50/60"}`}
                      data-testid={`dv3-flow-disc-${d.key}`} data-stage={cur}>
                      <span className="text-[11px] text-slate-700 flex-1 min-w-0 truncate">{pickL(d, "zh", "name")}</span>
                      <select value={cur} data-testid={`dv3-flow-disc-sel-${d.key}`}
                        onChange={(e) => (e.target.value ? assign(d.key, e.target.value) : unset(d.key))}
                        className="text-[10px] border border-slate-200 rounded px-1.5 py-0.5 bg-white max-w-[240px]">
                        <option value="">{T("不要求（未关联）")}</option>
                        {stages.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
                      </select>
                    </div>
                  );
                })}
                {discs.filter(discHit).length === 0 && <p className="text-[11px] text-slate-400 py-2">{T("没有匹配的产物")}</p>}
              </div>
            ) : (
              <div className="mt-2 space-y-1" data-testid="dv3-flow-by-stage">
                {stages.filter((s) => stageHit(s) || discs.some((d) => discHit(d) && map[d.key] === s.key)).map((s) => {
                  const mine = discs.filter((d) => map[d.key] === s.key);
                  const rest = discs.filter((d) => map[d.key] !== s.key && !removed.includes(d.key) && discHit(d));
                  return (
                    <div key={s.key} className="rounded-md border border-slate-100 bg-slate-50/70 px-2 py-1.5" data-testid={`dv3-flow-stage-${s.key}`}>
                      <div className="flex items-center gap-2">
                        <b className="text-[11px] text-slate-700 flex-1 truncate">{s.name}</b>
                        <span className={`text-[10px] tabular-nums ${mine.length ? "text-slate-500" : "text-amber-700"}`}>{mine.length ? T("{0} 项", [mine.length]) : T("无产物")}</span>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-1">
                        {mine.map((d) => (
                          <span key={d.key} className={`inline-flex items-center gap-1 rounded-full text-[10px] font-semibold px-2 py-0.5 ${flowQn && discHit(d) ? "bg-violet-600 text-white" : "bg-violet-100 text-violet-800"}`}>
                            {pickL(d, "zh", "name")}
                            <button type="button" data-testid={`dv3-flow-rm-${s.key}-${d.key}`} onClick={() => unset(d.key)} className={flowQn && discHit(d) ? "text-violet-200 hover:text-white" : "text-violet-500 hover:text-violet-900"}><X className="h-3 w-3" /></button>
                          </span>
                        ))}
                        {mine.length === 0 && <span className="text-[10px] text-slate-400">{T("该阶段不要求任何产物")}</span>}
                        {rest.length > 0 && (
                          <select value="" data-testid={`dv3-flow-add-${s.key}`} onChange={(e) => e.target.value && assign(e.target.value, s.key)}
                            className="text-[10px] border border-dashed border-slate-300 rounded-full px-1.5 py-0.5 text-slate-500 bg-white">
                            <option value="">+ {T("添加产物")}</option>
                            {rest.map((d) => <option key={d.key} value={d.key}>{pickL(d, "zh", "name")}</option>)}
                          </select>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {removed.length > 0 && (
              <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5" data-testid="dv3-flow-unmounted">
                <b className="text-[11px] text-amber-800">{T("未关联任何阶段（系统不要求）")}</b>
                <div className="mt-1 flex flex-wrap gap-1">
                  {removed.map((k) => {
                    const d = discs.find((x) => x.key === k);
                    return d ? (
                      <button key={k} type="button" onClick={() => setRemoved((r) => r.filter((x) => x !== k))}
                        title={T("点一下恢复，再选阶段")}
                        className="inline-flex items-center gap-1 rounded-full bg-white border border-amber-300 text-amber-800 text-[10px] px-2 py-0.5">
                        {pickL(d, "zh", "name")}<RotateCcw className="h-3 w-3" />
                      </button>
                    ) : null;
                  })}
                </div>
              </div>
            )}
          </>}

          {tab === "discag" && <>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[11px] text-slate-400 flex-1 min-w-[240px]">
                {T("先区分设计交付与人工办理：人工办理项由机构状态自动推进产物并结束计时；再决定每项挂哪个机构。机构必需文件与移交门禁不变。")}
              </p>
              <span className="text-[11px] font-semibold text-slate-600 tabular-nums shrink-0" data-testid="dv3-discag-summary">
                {T("已挂机构 {0}/{1} 项产物", [discs.filter((d) => (daMap[d.key] || []).length > 0).length, discs.length])}
              </span>
            </div>
            <div className="mt-2 relative">
              <Search className="h-3.5 w-3.5 absolute left-2 top-1.5 text-slate-400" />
              <input value={daQ} onChange={(e) => setDaQ(e.target.value)} placeholder={T("搜产物")} data-testid="dv3-discag-search" className="text-xs border border-slate-200 rounded-md pl-7 pr-2 py-1 w-44" />
            </div>
            <div className="mt-2 space-y-1" data-testid="dv3-discag-list">
              {discs.filter((d) => !daQ.trim() || `${d.name} ${d.zh || ""}`.toLowerCase().includes(daQ.trim().toLowerCase())).map((d) => {
                const mine = daMap[d.key] || [];
                const rest = daTypes.filter((t) => !mine.includes(t));
                return (
                  <div key={d.key} className="flex items-start gap-2 rounded-md border border-slate-100 bg-slate-50/70 px-2 py-1" data-testid={`dv3-discag-${d.key}`} data-n={mine.length}>
                    <span className="text-[11px] text-slate-700 w-24 shrink-0 truncate pt-0.5">{pickL(d, "zh", "name")}</span>
                    <select value={discKinds[d.key] || d.discKind || "design"} data-testid={`dv3-disckind-${d.key}`}
                      onChange={(e) => setDiscKinds((m) => ({ ...m, [d.key]: e.target.value }))}
                      className="shrink-0 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">
                      <option value="design">{T("设计交付")}</option>
                      <option value="manual">{T("人工办理")}</option>
                    </select>
                    <div className="flex flex-wrap items-center gap-1 flex-1 min-w-0">
                      {mine.map((t) => (
                        <span key={t} className="inline-flex items-center gap-1 rounded-full bg-teal-100 text-teal-800 text-[10px] font-semibold px-2 py-0.5">
                          {t}
                          <button type="button" data-testid={`dv3-discag-rm-${d.key}-${t.replace(/[^A-Za-z0-9]/g, "")}`}
                            onClick={() => setDaMap((m) => ({ ...m, [d.key]: (m[d.key] || []).filter((x) => x !== t) }))}
                            className="text-teal-600 hover:text-teal-900"><X className="h-3 w-3" /></button>
                        </span>
                      ))}
                      {mine.length === 0 && <span className="text-[10px] text-slate-400">{T("不挂机构（设计内部产物）")}</span>}
                      {rest.length > 0 && (
                        <select value="" data-testid={`dv3-discag-add-${d.key}`}
                          onChange={(e) => e.target.value && setDaMap((m) => ({ ...m, [d.key]: [...(m[d.key] || []), e.target.value] }))}
                          className="text-[10px] border border-dashed border-slate-300 rounded-full px-1.5 py-0.5 text-slate-500 bg-white max-w-[200px]">
                          <option value="">+ {T("挂机构")}</option>
                          {rest.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="mt-2 text-[11px] text-slate-400">{T("默认按名称语义配好（消防↔消防局、结构↔结构工程师…）。实际业务里某项产物要找别的机构，就在这里改。")}</p>
          </>}

          {tab === "discstat" && <>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[11px] text-slate-400 flex-1 min-w-[240px]">
                {T("按阶段列出产物，决定每项产物在会话、矩阵和详情里能选哪些状态。默认已按业务语义区分设计交付与人工办理；旧项目若停在未勾选状态，仍会保留为当前状态。")}
              </p>
              <span className="text-[11px] font-semibold text-slate-600 tabular-nums shrink-0" data-testid="dv3-discstat-summary">
                {T("已配置 {0} 项产物", [discs.filter((d) => (dsMap[d.key] || []).length > 0).length])}
              </span>
            </div>
            <div className="mt-2 relative">
              <Search className="h-3.5 w-3.5 absolute left-2 top-1.5 text-slate-400" />
              <input value={dsQ} onChange={(e) => setDsQ(e.target.value)} placeholder={T("搜阶段 / 产物")} data-testid="dv3-discstat-search"
                className="text-xs border border-slate-200 rounded-md pl-7 pr-2 py-1 w-44" />
            </div>
            {/* 按阶段分组（2026-10-19 用户需求「产物状态也是…」）：
                分组与顺序来自后端 stages（与「全流程责任」「流程↔产物」同源），
                这样用户点开某阶段看到的产物清单，与项目详情里的阶段是对得上的。
                stages 为空时（旧后端 / 未配置挂载）退回原来的平铺列表，不丢功能。 */}
            {/* 间距与「全流程责任」页签同一套层级节奏（2026-10-20）：
                阶段 ↔ 阶段 20px、阶段标题 ↔ 产物列表 10px、产物之间 4px。
                两页结构相同就该用同一种视觉密度，否则切页签会有「换了套系统」的错觉。 */}
            <div className="mt-3 space-y-5" data-testid="dv3-discstat-list">
              {(dsStages.length ? dsStages : [{ key: "__all", name: "", disciplines: discs.map((d) => d.key) }]).map((st) => {
                const mine = (st.disciplines || []).filter((k) => {
                  const d = discs.find((x) => x.key === k);
                  if (!d) return false;
                  return !dsQ.trim() || `${d.name} ${d.zh || ""} ${st.name || ""}`.toLowerCase().includes(dsQ.trim().toLowerCase());
                });
                if (!mine.length) return null;
                return (
                  <div key={st.key} className="rounded-lg border border-slate-200 bg-slate-50/60 p-2.5" data-testid={`dv3-discstat-stage-${st.key}`}>
                    {st.name && (
                      <div className="flex items-center gap-2 border-b border-slate-200/80 pb-1.5">
                        <b className="text-[11px] text-slate-700">{T(st.name)}</b>
                        <span className="text-[10px] text-slate-400 tabular-nums">{T("{0} 项产物", [mine.length])}</span>
                      </div>
                    )}
                    <div className="mt-2.5 space-y-1">
                      {mine.map((k) => {
                        const d = discs.find((x) => x.key === k) || { key: k, name: k, zh: k };
                        const kind = kindOf(d);
                        const selected = (dsMap[k] || []).slice().sort((a, b) => dsStatuses.indexOf(a) - dsStatuses.indexOf(b));
                        const rest = dsStatuses.filter((s) => !selected.includes(s));
                        return (
                          <div key={k} className="flex items-start gap-2 rounded-md border border-slate-200 bg-white px-2 py-1.5" data-testid={`dv3-discstat-${k}`} data-n={selected.length}>
                            <span className="text-[11px] text-slate-700 w-28 shrink-0 truncate pt-0.5" title={pickL(d, "zh", "name")}>{pickL(d, "zh", "name")}</span>
                            <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold ${kind === "manual" ? "bg-teal-100 text-teal-800" : "bg-violet-100 text-violet-800"}`}>
                              {kind === "manual" ? T("人工办理") : T("设计交付")}
                            </span>
                            <div className="flex flex-wrap items-center gap-1 flex-1 min-w-0">
                              {selected.map((s) => (
                                <span key={s} className="inline-flex items-center gap-1 rounded-full bg-white border border-slate-200 text-slate-700 text-[10px] font-semibold px-2 py-0.5">
                                  {statusLabel(s, kind)}
                                  <button type="button" data-testid={`dv3-discstat-rm-${k}-${s}`} onClick={() => removeDiscStatus(k, s)}
                                    className="text-slate-400 hover:text-red-700" title={T("移除")}><X className="h-3 w-3" /></button>
                                </span>
                              ))}
                              {rest.length > 0 && (
                                <select value="" data-testid={`dv3-discstat-add-${k}`} onChange={(e) => { addDiscStatus(k, e.target.value); e.target.value = ""; }}
                                  className="text-[10px] border border-dashed border-slate-300 rounded-full px-1.5 py-0.5 text-slate-500 bg-white max-w-[150px]">
                                  <option value="">+ {T("添加状态")}</option>
                                  {rest.map((s) => <option key={s} value={s}>{statusLabel(s, kind)}</option>)}
                                </select>
                              )}
                            </div>
                            <button type="button" data-testid={`dv3-discstat-reset-${k}`} onClick={() => resetDiscStatus(k)}
                              title={T("恢复该项默认状态")} className="shrink-0 h-6 w-6 inline-flex items-center justify-center rounded-md text-slate-400 hover:bg-white hover:text-violet-700">
                              <RotateCcw className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="mt-2 text-[11px] text-slate-400">{T("人工办理项的「办理中 / 已递交 / 补件整改」只是同一状态键的业务文案，不改变计时、自动化或数据留痕。")}</p>
          </>}

          {tab === "party" && <>
            {/* 需求（2026-10-19 用户第三次澄清）：「所有的东西还需要明确是谁来提交..
                谁来推进..谁来给到外部这些」。所以这一页按**项目详情的阶段顺序**列出
                三类对象，每类只显示它真正需要的责任项：
                  · 交付物  → 谁提交 / 谁审核
                  · 机构    → 谁办理（给到外部）/ 谁推进（盯批复）
                  · 阶段任务 → 谁负责推进
                配的仍是**身份**（美国设计师 / 中国设计师 / 交图员，任务多一个设计部主管），
                具体是谁由项目的三个岗位决定；对全部项目生效，不改历史数据。 */}
            <p className="text-[11px] text-slate-400">
              {T("按阶段列出这个流程里的每一件事：交付物配「谁来提交 / 谁来审核」，机构对接配「谁来办理（给到外部）/ 谁来推进」，阶段任务配「谁负责推进」。这里配的是身份（美国设计师 / 中国设计师 / 交图员），具体是谁由项目的三个岗位决定；配置对全部项目生效。")}
            </p>
            {prRo && <p className="mt-1.5 text-[11px] text-amber-700" data-testid="dv3-party-ro">{T("责任分工只有设计主管 / admin / CEO 能改。")}</p>}
            {/* 签约前两步默认不列出：项目详情和矩阵早已对签约后项目隐藏它们，
                这里若继续显示，会让人以为流程还要求「前期方案咨询 / 合同签署」。
                但它们对「签约前营销方案」项目仍真实生效（政策核实门禁、合同附件校验），
                所以保留一个可展开的入口，而不是直接从配置里删掉。 */}
            {preSignStages.length > 0 && (
              <div className="mt-1.5 flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] text-slate-500" data-testid="dv3-party-presign-note">
                <span>{T("签约前两步（前期方案咨询 / 合同签署）在项目管理里已不展示，这里默认收起；签约前营销方案项目仍按原配置生效。")}</span>
                <button type="button" data-testid="dv3-party-presign-toggle" data-on={showPreSign ? "1" : "0"}
                  onClick={() => setShowPreSign((v) => !v)}
                  className="ml-auto shrink-0 font-semibold text-violet-700 hover:underline">
                  {showPreSign ? T("收起签约前阶段") : T("显示签约前阶段")}
                </button>
              </div>
            )}
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="h-3.5 w-3.5 absolute left-2 top-1.5 text-slate-400" />
                <input value={prQ} onChange={(e) => setPrQ(e.target.value)} placeholder={T("搜阶段 / 产物 / 机构 / 任务")} data-testid="dv3-party-search"
                  className="text-xs border border-slate-200 rounded-md pl-7 pr-2 py-1 w-48" />
              </div>
              <span className={`text-[11px] font-semibold tabular-nums ${(prNoSubmit.length || agEmpty.length || taskEmpty.length) ? "text-red-600" : "text-slate-500"}`} data-testid="dv3-party-summary">
                {(prNoSubmit.length || agEmpty.length || taskEmpty.length)
                  ? T("{0} 项还没配齐责任人，保存会被拒绝", [prNoSubmit.length + agEmpty.length + taskEmpty.length])
                  : T("共 {0} 项已配置", [prTotal])}
              </span>
            </div>
            {/* 层级间距（2026-10-20 用户反馈「阶段和产物的层级间距明显一点」）：
                原来阶段卡片之间 space-y-2(8px)，阶段内三类对象分组之间 mt-1.5(6px)，
                两层只差 2px，扫视时阶段标题和产物卡片糊成一片。
                现在按 20 / 10 / 4 的三级倍差排版：
                  阶段 ↔ 阶段 20px（space-y-5）
                  阶段内 产物组/机构组/任务组 之间 10px（mt-2.5）
                  同一组内单个产物/机构/任务 之间 4px（space-y-1）
                倍差比等比微调更容易被眼睛识别成「不同的层」，而不是「同一层没对齐」。 */}
            <div className="mt-3 space-y-5" data-testid="dv3-party-list">
              {prVisible.map((s) => {
                // 搜索命中：阶段名 / 任一对象名都能过滤，但阶段卡片结构不变，
                // 用户始终能看出「这一项属于哪个阶段」。
                const hit = (text) => !prQn || `${text} ${s.name}`.toLowerCase().includes(prQn);
                /* 与项目详情 / 矩阵阶段会话同口径去重（2026-10-21 用户要求「还有全阶段责任
                   设置里面也是」）：本阶段若同时有「配对产物」和「配对任务」，只保留任务这一行。
                   理由同前两处 —— 这两行说的是同一份交付，只是数据模型分了两套；
                   详情里已经把产物卡收起、只留任务卡，配置页若仍列两行，
                   用户会以为要分别配两遍责任人，而且改了一边另一边不动。
                   边界（不能整体砍）：
                     · 只在本阶段**真的有那条任务**时才收起产物行；没有配对任务的人工办理项 /
                       机构类产物照常列出，否则那些产物就再也没有地方配提交与审核身份。
                     · 收起只影响「长什么样」，保存仍提交完整的 map：被收起的产物沿用原配置提交，
                       后端归一化也不会把没展示过的配置清掉（与 preSignStages 折叠同一处理）。 */
                const taskTitles = new Set((s.tasks || []).map((t) => t.title));
                const carriedDiscKeys = new Set((s.disciplines || []).filter((k) => taskTitles.has(DISC_TO_TASK[k])).map((k) => k));
                const mineDisc = (s.disciplines || []).filter((k) => hit(discLabel(k)));
                const mineDiscShown = mineDisc.filter((k) => !carriedDiscKeys.has(k));
                const mineAg = (s.agencies || []).filter((t) => hit(t));
                const mineTask = (s.tasks || []).filter((t) => hit(t.title));
                if (!mineDiscShown.length && !mineAg.length && !mineTask.length) return null;
                return (
                  <div key={s.key} className="rounded-lg border border-slate-200 bg-slate-50/60 p-2.5" data-testid={`dv3-party-stage-${s.key}`}>
                    <div className="flex items-center gap-2 border-b border-slate-200/80 pb-1.5">
                      <b className="text-[11px] text-slate-700">{T(s.name)}</b>
                      <span className="text-[10px] text-slate-400 tabular-nums">
                        {T("{0} 项产物 · {1} 家机构 · {2} 项任务", [(s.disciplines || []).length, (s.agencies || []).length, (s.tasks || []).length])}
                      </span>
                    </div>

                    {/* ① 交付物：谁来提交 / 谁来审核（只列没被任务行承载的；见上方 carriedDiscKeys） */}
                    {mineDiscShown.length > 0 && (
                      <div className="mt-2.5 space-y-1" data-testid={`dv3-party-discs-${s.key}`}>
                        {mineDiscShown.map((k) => {
                          const cfg = prOf(k);
                          return (
                            <div key={k} className="rounded-md border border-slate-200 bg-white px-2 py-1.5"
                              data-testid={`dv3-party-disc-${k}`} data-submit-roles={(cfg.submitRoles || []).join(",")}
                              data-review-roles={(cfg.reviewRoles || []).join(",")}>
                              <div className="flex items-center gap-2">
                                <span className="shrink-0 rounded bg-violet-100 px-1.5 py-0.5 text-[9px] font-bold text-violet-700">{T("交付物")}</span>
                                <span className="min-w-0 flex-1 truncate text-[11px] font-semibold text-slate-700">{discLabel(k)}</span>
                                <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold ${(cfg.reviewRoles || []).length ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}
                                  data-testid={`dv3-party-mode-${k}`}>
                                  {(cfg.reviewRoles || []).length ? T("需要审核") : T("无需审核")}
                                </span>
                              </div>
                              <div className="mt-1 flex flex-wrap items-center gap-1">
                                <span className="w-14 shrink-0 text-[9px] text-slate-500">{T("谁来提交")}</span>
                                {prRoles.map((r) => {
                                  const on = (cfg.submitRoles || []).includes(r.key);
                                  return (
                                    <button key={r.key} type="button" disabled={prRo}
                                      data-testid={`dv3-party-submit-${k}-${r.key}`} data-on={on ? "1" : "0"}
                                      onClick={() => toggleRole(k, "submitRoles", r.key)}
                                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold disabled:opacity-60 ${on ? "bg-violet-600 text-white" : "border border-slate-200 bg-white text-slate-500 hover:border-violet-300"}`}>
                                      {T(r.label)}
                                    </button>
                                  );
                                })}
                                {!(cfg.submitRoles || []).length && <span className="text-[10px] font-semibold text-red-600">{T("至少选一个")}</span>}
                              </div>
                              <div className="mt-1 flex flex-wrap items-center gap-1">
                                <span className="w-14 shrink-0 text-[9px] text-slate-500">{T("谁来审核")}</span>
                                {prRoles.map((r) => {
                                  const on = (cfg.reviewRoles || []).includes(r.key);
                                  return (
                                    <button key={r.key} type="button" disabled={prRo}
                                      data-testid={`dv3-party-review-${k}-${r.key}`} data-on={on ? "1" : "0"}
                                      onClick={() => toggleRole(k, "reviewRoles", r.key)}
                                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold disabled:opacity-60 ${on ? "bg-sky-600 text-white" : "border border-slate-200 bg-white text-slate-500 hover:border-sky-300"}`}>
                                      {T(r.label)}
                                    </button>
                                  );
                                })}
                                <span className="text-[9px] text-slate-400" data-testid={`dv3-party-review-hint-${k}`}>
                                  {(cfg.reviewRoles || []).length ? T("提交后由选中的身份审核，通过才算完成") : T("三个身份都不选 = 无需审核，提交即完成")}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* ② 机构对接：谁来办理（给到外部）/ 谁来推进 */}
                    {mineAg.length > 0 && (
                      <div className="mt-2.5 space-y-1" data-testid={`dv3-party-agencies-${s.key}`}>
                        {mineAg.map((t) => {
                          const cfg = agOf(t);
                          return (
                            <div key={t} className="rounded-md border border-slate-200 bg-white px-2 py-1.5"
                              data-testid={`dv3-party-agency-${t.replace(/[^A-Za-z0-9]/g, "")}`}
                              data-handle-roles={(cfg.handleRoles || []).join(",")} data-advance-roles={(cfg.advanceRoles || []).join(",")}>
                              <div className="flex items-center gap-2">
                                <span className="shrink-0 rounded bg-teal-100 px-1.5 py-0.5 text-[9px] font-bold text-teal-700">{T("机构")}</span>
                                <span className="min-w-0 flex-1 truncate text-[11px] font-semibold text-slate-700" title={t}>{t}</span>
                                {(!(cfg.handleRoles || []).length || !(cfg.advanceRoles || []).length) && <span className="shrink-0 text-[10px] font-semibold text-red-600">{T("未配齐")}</span>}
                              </div>
                              <div className="mt-1 flex flex-wrap items-center gap-1">
                                <span className="w-14 shrink-0 text-[9px] text-slate-500">{T("谁来办理")}</span>
                                {prRoles.map((r) => {
                                  const on = (cfg.handleRoles || []).includes(r.key);
                                  return (
                                    <button key={r.key} type="button" disabled={prRo}
                                      data-testid={`dv3-party-handle-${t.replace(/[^A-Za-z0-9]/g, "")}-${r.key}`} data-on={on ? "1" : "0"}
                                      onClick={() => togglePill(setAgResp, t, "handleRoles", r.key)}
                                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold disabled:opacity-60 ${on ? "bg-teal-600 text-white" : "border border-slate-200 bg-white text-slate-500 hover:border-teal-300"}`}>
                                      {T(r.label)}
                                    </button>
                                  );
                                })}
                                <span className="text-[9px] text-slate-400">{T("把资料交到这家机构（给到外部）")}</span>
                              </div>
                              <div className="mt-1 flex flex-wrap items-center gap-1">
                                <span className="w-14 shrink-0 text-[9px] text-slate-500">{T("谁来推进")}</span>
                                {prRoles.map((r) => {
                                  const on = (cfg.advanceRoles || []).includes(r.key);
                                  return (
                                    <button key={r.key} type="button" disabled={prRo}
                                      data-testid={`dv3-party-advance-${t.replace(/[^A-Za-z0-9]/g, "")}-${r.key}`} data-on={on ? "1" : "0"}
                                      onClick={() => togglePill(setAgResp, t, "advanceRoles", r.key)}
                                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold disabled:opacity-60 ${on ? "bg-amber-600 text-white" : "border border-slate-200 bg-white text-slate-500 hover:border-amber-300"}`}>
                                      {T(r.label)}
                                    </button>
                                  );
                                })}
                                <span className="text-[9px] text-slate-400">{T("跟进进度、催办、盯批复")}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* ③ 阶段任务：谁负责推进（多一个设计部主管） */}
                    {mineTask.length > 0 && (
                      <div className="mt-2.5 space-y-1" data-testid={`dv3-party-tasks-${s.key}`}>
                        {mineTask.map((t) => {
                          const cfg = taskOf(t.key);
                          return (
                            <div key={t.key} className="rounded-md border border-slate-200 bg-white px-2 py-1.5"
                              data-testid={`dv3-party-task-${t.key.replace(/[^A-Za-z0-9]/g, "")}`}
                              data-owner-roles={(cfg.ownerRoles || []).join(",")}>
                              <div className="flex items-center gap-2">
                                <span className="shrink-0 rounded bg-slate-200 px-1.5 py-0.5 text-[9px] font-bold text-slate-600">{T("任务")}</span>
                                <span className="min-w-0 flex-1 truncate text-[11px] font-semibold text-slate-700" title={t.title}>{T(t.title)}</span>
                                {(t.files || []).length > 0 && <span className="shrink-0 text-[9px] text-slate-400">{T("需 {0}", [t.files.join("/")])}</span>}
                              </div>
                              <div className="mt-1 flex flex-wrap items-center gap-1">
                                <span className="w-14 shrink-0 text-[9px] text-slate-500">{T("谁来推进")}</span>
                                {prTaskRoles.map((r) => {
                                  const on = (cfg.ownerRoles || []).includes(r.key);
                                  return (
                                    <button key={r.key} type="button" disabled={prRo}
                                      data-testid={`dv3-party-task-own-${t.key.replace(/[^A-Za-z0-9]/g, "")}-${r.key}`} data-on={on ? "1" : "0"}
                                      onClick={() => togglePill(setTaskResp, t.key, "ownerRoles", r.key)}
                                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold disabled:opacity-60 ${on ? "bg-slate-800 text-white" : "border border-slate-200 bg-white text-slate-500 hover:border-slate-400"}`}>
                                      {T(taskRoleLabel(r.key))}
                                    </button>
                                  );
                                })}
                                {!(cfg.ownerRoles || []).length && <span className="text-[10px] font-semibold text-red-600">{T("至少选一个")}</span>}
                                {(cfg.ownerRoles || []).includes("manager") && <span className="text-[9px] text-slate-400">{T("主管认领，不自动指派到个人")}</span>}
                              </div>
                              {/* 配对任务：把这行实际承载的产物「谁交 / 谁审」一并显示出来（2026-10-21）。
                                  为什么写在这里：产物行已按去重收起（见上方 carriedDiscKeys），
                                  若不再露出提交 / 审核身份，配置页会出现「任务只配了推进，没人知道谁交谁审」
                                  的盲区 —— 而这一步的交付在项目里就是这份图纸。
                                  这里只读展示产物配置结果，编辑仍在上方产物行（未被收起的）或产物设置页，
                                  避免同一个配置在两处各写一套 state 造成互相覆盖。 */}
                              {DISC_TO_TASK && (() => {
                                const dk = Object.keys(DISC_TO_TASK).find((k) => DISC_TO_TASK[k] === t.title);
                                if (!dk) return null;
                                const dc = prOf(dk);
                                const sub = (dc.submitRoles || []).map(roleLabel).join(" / ") || T("未配置");
                                const rev = (dc.reviewRoles || []).map(roleLabel).join(" / ");
                                return (
                                  <p className="mt-1 flex flex-wrap items-center gap-1 text-[9px] text-slate-500" data-testid={`dv3-party-task-pairdisc-${t.key.replace(/[^A-Za-z0-9]/g, "")}`}>
                                    <span className="rounded bg-violet-100 px-1 py-0.5 text-[9px] font-bold text-violet-700">{T("产物")}</span>
                                    <span className="truncate">{discLabel(dk)}</span>
                                    <span>· {T("提交")} <b className="font-semibold text-slate-700">{sub}</b></span>
                                    <span>· {T("审核")} <b className="font-semibold text-slate-700">{rev || T("无需审核")}</b></span>
                                  </p>
                                );
                              })()}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
              {!prStages.length && <p className="py-2 text-[11px] text-slate-400">{T("还没有配置「流程 ↔ 产物」，请先在那一页把产物挂到阶段上。")}</p>}
            </div>
            <p className="mt-2 text-[11px] text-slate-400">
              {T("提交人与审核人配成同一身份时，系统按「需要审核」处理，但会把该轮复核落到设计部经理，避免自己审自己。机构与任务的负责人会在新建项目时按身份自动落到项目的三个岗位上。")}
            </p>

            {/* ⑨ City 审图整改（2026-10-20 用户需求）
                「在 Planning 审流程和 Building 审流程中将 city 改为多次的……每一个 correction
                 列需要有人去上传 1. 图纸 2. response letter……整体项目进度延期 3 天……
                 还需要在这个配置中增加这个 correction 的责任人的配置」。
                两个审图阶段各配一组：图纸由出图岗交、回复函由对外沟通岗写；
                顺延天数放这里而不是写死，因为不同城市 / 项目的 plan check 周期并不一致。 */}
            <div className="mt-4 rounded-lg border border-orange-200 bg-orange-50/40 p-2.5" data-testid="dv3-party-correction">
              <div className="flex flex-wrap items-center gap-2 border-b border-orange-200/70 pb-1.5">
                <b className="text-[11px] text-orange-900">{T("City 审图整改（correction）")}</b>
                <span className="text-[10px] text-slate-500">
                  {T("Planning / Building Review 被 City 退回时，每新增一轮整改需要交「修改图纸 + 回复函」，并把项目整体顺延。")}
                </span>
                {crRo && <span className="text-[10px] font-semibold text-amber-700" data-testid="dv3-party-correction-ro">{T("只能由设计主管修改")}</span>}
              </div>

              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-semibold text-slate-600">{T("每新增一轮整改，项目整体顺延")}</span>
                <input type="number" min={0} max={60} disabled={crRo}
                  value={crCfg?.delayDays ?? 3}
                  onChange={(e) => setCrCfg((c) => ({ ...(c || {}), delayDays: e.target.value }))}
                  data-testid="dv3-party-correction-days"
                  className="w-16 rounded-md border border-slate-200 px-1.5 py-0.5 text-[11px] disabled:bg-slate-50" />
                <span className="text-[10px] text-slate-600">{T("天（同时改项目目标完成日与该阶段及其后所有未完成阶段的截止日）")}</span>
              </div>

              <div className="mt-2 space-y-1.5">
                {crStages.map((st) => {
                  const cfg = (crCfg?.roles || {})[st.key] || {};
                  return (
                    <div key={st.key} className="rounded-md border border-slate-200 bg-white px-2 py-1.5"
                      data-testid={`dv3-party-correction-stage-${st.key}`} data-merged={st.merged ? "1" : "0"}>
                      <div className="flex items-center gap-2">
                        <span className={`shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold ${st.merged ? "bg-teal-100 text-teal-700" : "bg-orange-100 text-orange-700"}`}>{st.merged ? T("P+B 合并") : T("审图阶段")}</span>
                        <span className="min-w-0 flex-1 truncate text-[11px] font-semibold text-slate-700">{T(st.label)}</span>
                        {/* 合并行只对 concurrent 项目生效（2026-10-22）：
                            不写这一句的话，主管会以为改这里也能影响分开提交的项目，
                            而分开提交项目读的是上面两个阶段各自的配置。 */}
                        {st.merged && <span className="shrink-0 text-[9px] text-slate-400">{T("仅 P+B 同步提交项目生效")}</span>}
                      </div>
                      {crSlots.map((slot) => {
                        const on = cfg[slot.key] || [];
                        return (
                          <div key={slot.key} className="mt-1 flex flex-wrap items-center gap-1">
                            <span className="w-20 shrink-0 text-[9px] text-slate-500">{T(slot.label)}</span>
                            {crRoles.map((r) => {
                              const active = on.includes(r.key);
                              return (
                                <button key={r.key} type="button" disabled={crRo}
                                  data-testid={`dv3-party-correction-${st.key}-${slot.key}-${r.key}`}
                                  data-on={active ? "1" : "0"}
                                  onClick={() => setCrCfg((c) => {
                                    const cur = c || { delayDays: 3, roles: {} };
                                    const stage = { ...((cur.roles || {})[st.key] || {}) };
                                    const list = stage[slot.key] || [];
                                    stage[slot.key] = list.includes(r.key) ? list.filter((x) => x !== r.key) : [...list, r.key];
                                    return { ...cur, roles: { ...(cur.roles || {}), [st.key]: stage } };
                                  })}
                                  className={`rounded-full px-2 py-0.5 text-[10px] font-semibold disabled:opacity-60 ${active ? "bg-orange-600 text-white" : "border border-slate-200 bg-white text-slate-500 hover:border-orange-300"}`}>
                                  {T(r.label)}
                                </button>
                              );
                            })}
                            {!on.length && <span className="text-[10px] font-semibold text-red-600">{T("至少选一个")}</span>}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
              <p className="mt-1.5 text-[10px] text-slate-500">
                {T("顺延后：项目目标完成日 +N 天，该审图阶段及其后所有未完成阶段 / 子任务截止日 +N 天（保留基线，记入改期历史）。")}
              </p>
            </div>

            {/* ⑩ Land Survey（2026-10-20 用户需求）
                「增加 land survey 的可选，有的是 2 周、有的是 1 个月」→ 追问后明确
                「单独显示在下面的前期先放宽一点，然后让他们自己能够配，他们就是三个负责人」
                「就加在之前的全流程责任里面」。
                所以这里只配「谁负责 + 跟进间隔」。
                **不再有「默认预计天数」**：用户看到界面后明确否掉了这个字段
                （「这个 14 天不是说了不要了吗……只要实际交付时间就行」），
                工期改为只在标记完成时按 `完成日 − 登记日` 的实际天数顺延。
                跟进间隔（2026-10-20 第二轮）：用户原话「这个负责人需要没隔三天有一个待办」，
                默认 3 天，可在这里改 —— 测绘周期长的项目不必被每 3 天打扰一次。
                与 City 整改面板并列而不是另开页签：都属于「前期等外部单位」的责任配置。 */}
            <div className="mt-3 rounded-lg border border-sky-200 bg-sky-50/40 p-2.5" data-testid="dv3-party-landsurvey">
              <div className="flex flex-wrap items-center gap-2 border-b border-sky-200/70 pb-1.5">
                <b className="text-[11px] text-sky-900">{T("Land Survey（土地测绘 · 可选）")}</b>
                <span className="text-[10px] text-slate-500">
                  {T("方案设计阶段可选：需要时由项目负责人一键开始计时，标记完成时按实际交付天数顺延工期；负责人每 N 天收到一条跟进待办。")}
                </span>
                {lsRo && <span className="text-[10px] font-semibold text-amber-700" data-testid="dv3-party-landsurvey-ro">{T("只能由设计主管修改")}</span>}
              </div>

              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-semibold text-slate-600">{T("跟进待办间隔")}</span>
                <input type="number" min={1} max={30} disabled={lsRo}
                  value={lsCfg?.followupDays ?? 3}
                  onChange={(e) => setLsCfg((c) => ({ ...(c || {}), followupDays: e.target.value }))}
                  data-testid="dv3-party-landsurvey-followup"
                  className="w-16 rounded-md border border-slate-200 px-1.5 py-0.5 text-[11px] disabled:bg-slate-50" />
                <span className="text-[10px] text-slate-600">{T("天（1–30；登记后每 N 天给负责人生成一条跟进待办，提交后 N 天再来一条，直到标记完成）")}</span>
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1.5"
                data-testid="dv3-party-landsurvey-roles">
                <span className="w-24 shrink-0 text-[10px] font-semibold text-slate-600">{T("谁负责跟进测绘")}</span>
                {lsRoles.map((r) => {
                  const on = (lsCfg?.roles || []).includes(r.key);
                  return (
                    <button key={r.key} type="button" disabled={lsRo}
                      data-testid={`dv3-party-landsurvey-role-${r.key}`} data-on={on ? "1" : "0"}
                      onClick={() => setLsCfg((c) => {
                        const list = (c?.roles || []);
                        return { ...(c || {}), roles: on ? list.filter((x) => x !== r.key) : [...list, r.key] };
                      })}
                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold disabled:opacity-60 ${on ? "bg-sky-600 text-white" : "border border-slate-200 bg-white text-slate-500 hover:border-sky-300"}`}>
                      {T(r.label)}
                    </button>
                  );
                })}
                {!(lsCfg?.roles || []).length && <span className="text-[10px] font-semibold text-red-600">{T("至少选一个")}</span>}
                <span className="text-[10px] text-slate-400">{T("按项目三岗解析到具体账号；项目里的三个负责人都可自行登记")}</span>
              </div>
            </div>
          </>}

          {tab === "handoff" && <>
            <p className="text-[11px] text-slate-400">{T("移交工程部时必须上传的凭证。勾选的类别缺少真实文件时，移交和关闭「Building Approval + Permit」都会被拦下；不勾 = 不检查。名称可自行修改。")}</p>
            <div className="mt-3 space-y-1.5">
              {[["permit", T("施工许可证"), T("市政府签发的许可批件（Building Permit），带 Permit 号")],
                ["job_card", T("现场检查卡"), T("贴在工地现场的那张卡片，检查员到场必看（官方叫 Job Card）")]].map(([k, label, hint]) => (
                <div key={k} className={`rounded-lg border px-3 py-2 ${gateReq[k] ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}
                  data-testid={`dv3-handoff-gate-${k}`} data-on={gateReq[k] ? "1" : "0"}>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!gateReq[k]} data-testid={`dv3-handoff-gate-${k}-box`}
                      onChange={() => setGateReq((v) => ({ ...v, [k]: !v[k] }))} className="mt-0.5" />
                    <span className="min-w-0 flex-1">
                      <b className="block text-xs text-slate-800">{label}
                        <span className={`ml-1.5 text-[10px] font-bold px-1.5 py-px rounded-full ${gateReq[k] ? "bg-emerald-600 text-white" : "bg-slate-300 text-slate-600"}`}
                          data-testid={`dv3-handoff-gate-${k}-state`}>{gateReq[k] ? T("需要") : T("不需要")}</span>
                      </b>
                      <span className="text-[11px] text-slate-500">{hint}</span>
                    </span>
                  </label>
                  <div className="mt-1.5 flex items-center gap-1.5">
                    <span className="text-[10px] text-slate-400 shrink-0">{T("显示名")}</span>
                    <input value={gateLabels[k] ?? ""} data-testid={`dv3-handoff-gate-${k}-label`}
                      onChange={(e) => setGateLabels((v) => ({ ...v, [k]: e.target.value }))}
                      placeholder={gate?.defaultLabels?.[k] || label}
                      className="flex-1 min-w-0 text-[11px] border border-slate-200 rounded-md px-2 py-0.5" />
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-slate-500" data-testid="dv3-handoff-gate-summary">
              {T("当前要求：")}<b>{(gateReq.permit || gateReq.job_card)
                ? [gateReq.permit && (gateLabels.permit || T("施工许可证")), gateReq.job_card && (gateLabels.job_card || T("现场检查卡"))].filter(Boolean).join(" + ")
                : T("不校验（有资料即可移交）")}</b>
            </p>
          </>}
        </div>

        <div className="mt-3 pt-2 border-t border-slate-100 flex items-center justify-end gap-2">
          <span className="mr-auto inline-flex items-center gap-2">
            <span className="text-[10px] text-slate-400" data-testid="dv3-disccfg-hint">{T("保存会同时写入全部页签")}</span>
            {mapDirty && <span className="text-[11px] font-semibold text-amber-700" data-testid="dv3-disccfg-dirty">{T("有未保存的改动")}</span>}
          </span>
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs">{T("取消")}</button>
          <button onClick={save} disabled={busy} data-testid="dv3-disccfg-save" className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : T("保存")}
          </button>
        </div>
        {/* 阶段工期弹窗：保存后重新拉一次 disc-cfg，让只读的总周期立刻刷新，不用关掉重开整个设置窗 */}
        {stageDlg && (
          <StageDaysDialog
            api={api}
            onClose={() => setStageDlg(false)}
            onSaved={() => api.get("/design/disc-cfg")
              .then(({ data }) => setDesignDays(String(data.designDays ?? "—")))
              .catch(() => {})}
          />
        )}
      </div>
    </div>,
    document.body
  );
};
